/**
 * What Heidi Work can do once a Nango provider is connected.
 * Keep in sync with BE `apis/v2/integrations/nango/providers.py` capability maps
 * and `service/tools/registrations/nango_integrations.py`.
 */

export type NangoCapabilityId =
  | 'calendar'
  | 'mail'
  | 'files'
  | 'onenote'
  | 'teams'
  | 'patient'
  | 'sheets'
  | 'docs'
  | 'meetings';

export interface NangoCapability {
  id: NangoCapabilityId;
  /** Short label for chips */
  label: string;
  /** One-line description for the connection card */
  description: string;
  /** Agent tool name(s) this unlocks */
  tools: string[];
  /** Writes require clinician approval in Work */
  requiresApproval?: boolean;
}

export const NANGO_CAPABILITIES: Record<NangoCapabilityId, NangoCapability> = {
  calendar: {
    id: 'calendar',
    label: 'Calendar',
    description: 'See, add, change, and cancel calendar events.',
    tools: [
      'integrations.calendar.list',
      'integrations.calendar.create',
      'integrations.calendar.update',
      'integrations.calendar.delete',
    ],
    requiresApproval: true,
  },
  mail: {
    id: 'mail',
    label: 'Mail',
    description: 'Search, read, draft, send, and reply to emails.',
    tools: [
      'integrations.mail.search',
      'integrations.mail.read',
      'integrations.mail.create_draft',
      'integrations.mail.send',
      'integrations.mail.reply',
      'integrations.query_patient',
    ],
    requiresApproval: true,
  },
  files: {
    id: 'files',
    label: 'Files',
    description: 'Find, open, and save letters and files.',
    tools: [
      'integrations.files.search',
      'integrations.files.read',
      'integrations.files.save',
    ],
    requiresApproval: true,
  },
  onenote: {
    id: 'onenote',
    label: 'OneNote',
    description: 'Create visit scratch notes and after-visit pages.',
    tools: ['integrations.onenote.create_page'],
    requiresApproval: true,
  },
  teams: {
    id: 'teams',
    label: 'Teams',
    description:
      'Find teams, channels, and members, read conversations and replies, and post updates.',
    tools: [
      'integrations.teams.list',
      'integrations.teams.list_channels',
      'integrations.teams.list_members',
      'integrations.teams.read_messages',
      'integrations.teams.read_replies',
      'integrations.teams.post_message',
    ],
    requiresApproval: true,
  },
  patient: {
    id: 'patient',
    label: 'Patient lookup',
    description: 'Find emails and calendar events about a patient.',
    tools: ['integrations.query_patient'],
  },
  sheets: {
    id: 'sheets',
    label: 'Spreadsheets',
    description: 'Read clinic lists and trackers, and add new rows.',
    tools: ['integrations.sheets.read_range', 'integrations.sheets.append_row'],
    requiresApproval: true,
  },
  docs: {
    id: 'docs',
    label: 'Documents',
    description: 'Read, create, and add to letters, protocols, and templates.',
    tools: [
      'integrations.docs.read',
      'integrations.docs.create',
      'integrations.docs.append_text',
    ],
    requiresApproval: true,
  },
  meetings: {
    id: 'meetings',
    label: 'Meetings',
    description: 'See meetings and telehealth sessions, and read transcripts.',
    tools: ['integrations.meetings.list', 'integrations.meetings.transcript'],
  },
};

/** Clinician-facing labels for agent tool names shown on the manage-connector sheet. */
export const NANGO_TOOL_LABELS: Record<string, string> = {
  'integrations.calendar.list': 'See events',
  'integrations.calendar.create': 'Add events',
  'integrations.calendar.update': 'Change events',
  'integrations.calendar.delete': 'Cancel events',
  'integrations.mail.search': 'Search mail',
  'integrations.mail.read': 'Read mail',
  'integrations.mail.create_draft': 'Create drafts',
  'integrations.mail.send': 'Send mail',
  'integrations.mail.reply': 'Reply to mail',
  'integrations.files.search': 'Search files',
  'integrations.files.read': 'Read files',
  'integrations.files.save': 'Save files',
  'integrations.onenote.create_page': 'Create pages',
  'integrations.teams.list': 'List teams',
  'integrations.teams.list_channels': 'List channels',
  'integrations.teams.list_members': 'List members',
  'integrations.teams.read_messages': 'Read messages',
  'integrations.teams.read_replies': 'Read replies',
  'integrations.teams.post_message': 'Post messages',
  'integrations.query_patient': 'Look up a patient',
  'integrations.sheets.read_range': 'Read spreadsheets',
  'integrations.sheets.append_row': 'Add spreadsheet rows',
  'integrations.docs.read': 'Read documents',
  'integrations.docs.create': 'Create documents',
  'integrations.docs.append_text': 'Add to documents',
  'integrations.meetings.list': 'List meetings',
  'integrations.meetings.transcript': 'Read transcripts',
};

export function getNangoToolLabel(toolName: string): string {
  const mapped = NANGO_TOOL_LABELS[toolName];
  if (mapped) return mapped;
  const leaf = toolName.split('.').pop() ?? toolName;
  const humanized = leaf.replaceAll('_', ' ');
  if (!humanized) return toolName;
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}

/** Display title for a Nango unique key. */
export const NANGO_PROVIDER_LABELS: Record<string, string> = {
  microsoft: 'Microsoft 365',
  outlook: 'Outlook',
  'google-mail': 'Gmail',
  gmail: 'Gmail',
  'google-calendar': 'Google Calendar',
  'google-drive': 'Google Drive',
  'google-docs': 'Google Docs',
  'google-sheet': 'Google Sheets',
  'google-meet': 'Google Meet',
  'microsoft-teams': 'Microsoft Teams',
  'microsoft-word': 'Microsoft Word',
  'microsoft-excel': 'Microsoft Excel',
  'one-note': 'OneNote',
  'one-drive': 'OneDrive',
  'sharepoint-online': 'SharePoint',
};

/**
 * Capability ids unlocked by each provider_config_key.
 *
 * Only list a capability whose tools are actually registered on the backend —
 * an entry here is a promise the agent has to keep. Providers we offer for
 * connection but cannot act on yet map to `[]`, which renders the
 * "tools not wired yet" state instead of a capability the agent will refuse.
 */
export const NANGO_PROVIDER_CAPABILITIES: Record<string, NangoCapabilityId[]> = {
  microsoft: ['calendar', 'mail', 'files', 'onenote', 'teams', 'patient'],
  outlook: ['calendar', 'mail', 'patient'],
  'google-mail': ['mail', 'patient'],
  gmail: ['mail', 'patient'],
  'google-calendar': ['calendar', 'patient'],
  'google-drive': ['files'],
  'google-docs': ['files', 'docs'],
  'google-sheet': ['files', 'sheets'],
  'google-meet': ['meetings'],
  'microsoft-teams': ['teams'],
  'microsoft-word': ['files'],
  'microsoft-excel': ['files'],
  'one-note': ['onenote'],
  'one-drive': ['files'],
  'sharepoint-online': ['files'],
};

/**
 * Connectors whose tools the clinician can allow, ask for, or block in Heidi.
 * Mirrors `PERMISSION_PROVIDERS` in ml-scribe `nango/tool_permissions.py`; the
 * BE 404s the permissions endpoints for any other key.
 */
export const NANGO_TOOL_PERMISSION_PROVIDERS: readonly string[] = [
  'outlook',
  'microsoft-word',
  'microsoft-excel',
  'one-drive',
  'sharepoint-online',
  'google-mail',
  'google-calendar',
  'google-drive',
  'google-sheet',
];

export function hasNangoToolPermissions(providerConfigKey: string): boolean {
  return NANGO_TOOL_PERMISSION_PROVIDERS.includes(providerConfigKey);
}

export function getNangoProviderLabel(providerConfigKey: string): string {
  return NANGO_PROVIDER_LABELS[providerConfigKey] ?? providerConfigKey;
}

export function getNangoCapabilitiesForProvider(
  providerConfigKey: string
): NangoCapability[] {
  const ids = NANGO_PROVIDER_CAPABILITIES[providerConfigKey] ?? [];
  return ids.map((id) => NANGO_CAPABILITIES[id]);
}
